import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import axios from "axios";
import { Loader2, Search, RotateCcw, X, Star, FileText, Image as ImgIcon, FileCheck2, MessageCircle, Trash2, Download, ExternalLink, Award, Settings, ChevronLeft, ChevronRight } from "lucide-react";
import { PROFESSIONS, COUNTRIES, STATES } from "@/data/mmp";

const API = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "") + "/api";
const ax = axios.create({ baseURL: API, timeout: 30000 });

const STATUSES = ["New", "Reviewed", "Shortlisted", "Contacted", "Archived"];
const STATUS_COLOR = {
  New: "bg-blue-500/20 text-blue-300",
  Reviewed: "bg-amber-500/20 text-amber-300",
  Shortlisted: "bg-emerald-500/20 text-emerald-300",
  Contacted: "bg-indigo-500/20 text-indigo-300",
  Archived: "bg-slate-500/20 text-slate-300",
};

const initials = (name) => (name || "?").split(" ").slice(0, 2).map((w) => w[0]?.toUpperCase() || "").join("") || "?";

export default function MmpAdmin() {
  const [stats, setStats] = useState({});
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(50);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ q: "", profession: "", status: "", country: "", state: "", gender: "", rating: "", recommended: "" });
  const [selected, setSelected] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [showTemplates, setShowTemplates] = useState(false);

  const stateList = useMemo(() => STATES[filters.country] || [], [filters.country]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, page_size: pageSize };
      Object.entries(filters).forEach(([k, v]) => { if (v !== "" && v !== null) params[k] = v; });
      const { data } = await ax.get("/admin-mmp/profiles", { params });
      setItems(data.items); setTotal(data.total);
      const s = await ax.get("/admin-mmp/stats"); setStats(s.data);
    } catch { toast.error("Failed to load profiles"); } finally { setLoading(false); }
  }, [filters, page, pageSize]);

  useEffect(() => { load(); }, [load]);

  const reset = () => setFilters({ q: "", profession: "", status: "", country: "", state: "", gender: "", rating: "", recommended: "" });

  const changeStatus = async (p, status) => {
    await ax.patch(`/admin-mmp/profiles/${p.unique_id}`, { status }); toast.success("Status updated"); if (selected?.unique_id === p.unique_id) setSelected({ ...selected, status }); await load();
  };
  const changeRating = async (p, rating) => {
    await ax.patch(`/admin-mmp/profiles/${p.unique_id}`, { rating }); if (selected?.unique_id === p.unique_id) setSelected({ ...selected, rating }); await load();
  };
  const toggleRec = async (p) => {
    await ax.patch(`/admin-mmp/profiles/${p.unique_id}`, { recommended: !p.recommended }); toast.success("Updated"); if (selected?.unique_id === p.unique_id) setSelected({ ...selected, recommended: !p.recommended }); await load();
  };
  const remove = async (p) => {
    if (!window.confirm(`Delete ${p.unique_id}?`)) return;
    await ax.delete(`/admin-mmp/profiles/${p.unique_id}`); toast.success("Deleted"); if (selected?.unique_id === p.unique_id) setSelected(null); await load();
  };

  const exportAll = () => window.open(`${API}/admin-mmp/export`, "_blank");
  const fileUrl = (path) => path ? `${API}/files/${path}` : null;

  const toggleSel = (id) => setSelectedIds((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const bulkExport = async () => {
    if (selectedIds.size === 0) return toast.info("Select profiles first");
    const rows = items.filter((i) => selectedIds.has(i.unique_id));
    const keys = ["unique_id", "full_name", "email", "phone", "profession", "country", "state", "status", "rating"];
    const csv = [keys.join(","), ...rows.map((r) => keys.map((k) => `"${(r[k] ?? "").toString().replace(/"/g, '""')}"`).join(","))].join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    const a = document.createElement("a"); a.href = url; a.download = "mmp_selected.csv"; a.click(); URL.revokeObjectURL(url);
  };

  const pages = Math.max(1, Math.ceil(total / pageSize));

  return (
    <div className="min-h-screen bg-[#0B0F1A] text-slate-100">
      <header className="border-b border-white/5 bg-[#0E1525]/80 backdrop-blur sticky top-0 z-20">
        <div className="mia-container flex items-center justify-between py-4">
          <div>
            <div className="text-[0.62rem] font-bold tracking-[0.24em] uppercase text-sky-400">Admin · MMP Portal</div>
            <div className="font-display font-black text-xl">Marine & Maritime Professionals</div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => setShowTemplates(true)} className="text-xs text-slate-300 hover:text-sky-300 inline-flex items-center gap-1.5 border border-white/10 rounded-lg px-3 py-2" data-testid="mmp-admin-templates-btn"><Settings className="w-4 h-4" /> Templates</button>
            <button onClick={exportAll} className="text-xs text-[#0B0F1A] bg-sky-500 hover:bg-sky-400 font-bold uppercase tracking-widest rounded-lg px-4 py-2 inline-flex items-center gap-1.5" data-testid="mmp-admin-export-all"><Download className="w-4 h-4" /> Export All</button>
          </div>
        </div>
      </header>

      <div className="mia-container py-8 space-y-6">
        {/* STATS */}
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3">
          {[["Total", stats.total], ["New", stats.new], ["Reviewed", stats.reviewed], ["Shortlisted", stats.shortlisted], ["Contacted", stats.contacted], ["Archived", stats.archived], ["Recommended", stats.recommended]].map(([label, v]) => (
            <div key={label} className="rounded-xl border border-white/10 bg-[#0E1525] px-4 py-3">
              <div className="text-[0.6rem] uppercase tracking-widest text-slate-400">{label}</div>
              <div className="text-2xl font-black mt-1">{v ?? "—"}</div>
            </div>
          ))}
        </div>

        {/* FILTERS */}
        <div className="rounded-xl border border-white/10 bg-[#0E1525] p-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <div className="lg:col-span-2 flex items-center gap-2 rounded-lg border border-white/10 bg-[#0B0F1A] px-3 py-2.5"><Search className="w-4 h-4 text-slate-500" /><input value={filters.q} onChange={(e) => { setFilters({ ...filters, q: e.target.value }); setPage(1); }} placeholder="Search name, email, ID, profession, country…" className="w-full bg-transparent focus:outline-none text-sm" data-testid="mmp-admin-search" /></div>
          <Sel value={filters.profession} onChange={(v) => { setFilters({ ...filters, profession: v }); setPage(1); }} testid="mmp-admin-fil-profession"><option value="">All Professions</option>{PROFESSIONS.map((p) => <option key={p} value={p}>{p}</option>)}</Sel>
          <Sel value={filters.status} onChange={(v) => { setFilters({ ...filters, status: v }); setPage(1); }} testid="mmp-admin-fil-status"><option value="">All Status</option>{STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}</Sel>
          <Sel value={filters.country} onChange={(v) => { setFilters({ ...filters, country: v, state: "" }); setPage(1); }} testid="mmp-admin-fil-country"><option value="">All Countries</option>{COUNTRIES.map((c) => <option key={c} value={c}>{c}</option>)}</Sel>
          <Sel value={filters.state} onChange={(v) => { setFilters({ ...filters, state: v }); setPage(1); }} disabled={stateList.length === 0} testid="mmp-admin-fil-state"><option value="">{stateList.length ? "All States" : "—"}</option>{stateList.map((s) => <option key={s} value={s}>{s}</option>)}</Sel>
          <Sel value={filters.gender} onChange={(v) => { setFilters({ ...filters, gender: v }); setPage(1); }} testid="mmp-admin-fil-gender"><option value="">Any Gender</option><option value="Male">Male</option><option value="Female">Female</option></Sel>
          <Sel value={filters.rating} onChange={(v) => { setFilters({ ...filters, rating: v }); setPage(1); }} testid="mmp-admin-fil-rating"><option value="">Any Rating</option>{[1, 2, 3, 4, 5].map((r) => <option key={r} value={r}>{r}★</option>)}</Sel>
          <div className="flex items-center gap-3">
            <label className="text-xs text-slate-300 inline-flex items-center gap-2"><input type="checkbox" checked={filters.recommended === "true"} onChange={(e) => { setFilters({ ...filters, recommended: e.target.checked ? "true" : "" }); setPage(1); }} data-testid="mmp-admin-fil-rec" /> Recommended only</label>
            <button onClick={reset} className="ml-auto text-xs text-slate-400 hover:text-sky-300 inline-flex items-center gap-1" data-testid="mmp-admin-fil-reset"><RotateCcw className="w-3.5 h-3.5" /> Reset</button>
          </div>
        </div>

        {/* BULK ACTIONS */}
        {selectedIds.size > 0 && (
          <div className="rounded-lg border border-sky-500/40 bg-sky-500/10 px-4 py-2.5 flex items-center gap-3 flex-wrap text-sm">
            <span className="font-semibold">{selectedIds.size} selected</span>
            <button onClick={bulkExport} className="inline-flex items-center gap-1.5 text-sky-200 hover:text-sky-100"><Download className="w-4 h-4" /> Bulk CSV</button>
            <button onClick={() => setSelectedIds(new Set())} className="text-slate-400 hover:text-slate-200 ml-auto">Clear</button>
          </div>
        )}

        {/* LIST */}
        <div className="rounded-xl border border-white/10 bg-[#0E1525] overflow-hidden">
          {loading ? (
            <div className="p-10 text-center text-slate-400"><Loader2 className="w-4 h-4 animate-spin inline mr-2" />Loading…</div>
          ) : items.length === 0 ? (
            <div className="p-10 text-center text-slate-400">No profiles match your filters.</div>
          ) : (
            <ul className="divide-y divide-white/5">
              {items.map((p) => (
                <li key={p.unique_id} className="p-4 sm:p-5 hover:bg-white/[0.02] flex items-center gap-4 flex-wrap sm:flex-nowrap relative" data-testid={`mmp-row-${p.unique_id}`}>
                  <input type="checkbox" checked={selectedIds.has(p.unique_id)} onChange={() => toggleSel(p.unique_id)} className="shrink-0" data-testid={`mmp-row-check-${p.unique_id}`} />
                  <Avatar path={p.passport_photo_path} name={p.full_name} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <div className="font-semibold text-white truncate">{p.full_name}</div>
                      {p.recommended && <span className="inline-flex items-center gap-1 text-[0.6rem] font-bold text-amber-300 bg-amber-500/15 px-2 py-0.5 rounded-full uppercase tracking-widest"><Award className="w-3 h-3" /> Recommended</span>}
                    </div>
                    <div className="font-mono text-xs text-slate-500 mt-0.5">{p.unique_id}</div>
                    <div className="flex items-center gap-2 mt-2 flex-wrap">
                      <Pill className="bg-sky-500/20 text-sky-300">{p.profession}</Pill>
                      <Pill className="bg-white/5 text-slate-300">{[p.city, p.state, p.country].filter(Boolean).join(", ")}</Pill>
                      <Pill className={STATUS_COLOR[p.status] || STATUS_COLOR.New}>{p.status}</Pill>
                    </div>
                    <div className="flex items-center gap-3 mt-2">
                      <Stars rating={p.rating || 0} onSet={(r) => changeRating(p, r)} />
                      <DocBadge label="CV" ok={!!p.cv_path} />
                      <DocBadge label="Photo" ok={!!p.passport_photo_path} />
                      <DocBadge label="Portfolio" ok={!!p.portfolio_path} />
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-wrap justify-end">
                    <select value={p.status} onChange={(e) => changeStatus(p, e.target.value)} className="bg-[#0B0F1A] border border-white/10 rounded-md text-xs px-2 py-1.5 focus:outline-none" data-testid={`mmp-row-status-${p.unique_id}`}>
                      {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                    <button onClick={() => toggleRec(p)} className={`p-2 rounded-md border border-white/10 hover:border-amber-400 ${p.recommended ? "text-amber-300" : "text-slate-400"}`} title="Toggle Recommended" data-testid={`mmp-row-rec-${p.unique_id}`}><Award className="w-4 h-4" /></button>
                    <a href={p.phone ? `https://wa.me/${p.phone.replace(/\D/g, "")}?text=${encodeURIComponent(`Hello ${p.full_name}, this is Marine Infrastructure regarding your profile (${p.unique_id}).`)}` : "#"} target="_blank" rel="noreferrer" className="p-2 rounded-md border border-white/10 hover:border-emerald-400 text-emerald-300" title="WhatsApp" data-testid={`mmp-row-wa-${p.unique_id}`}><MessageCircle className="w-4 h-4" /></a>
                    {p.cv_path && <a href={fileUrl(p.cv_path)} target="_blank" rel="noreferrer" className="p-2 rounded-md border border-white/10 hover:border-sky-400 text-sky-300" title="Download CV" data-testid={`mmp-row-cv-${p.unique_id}`}><FileText className="w-4 h-4" /></a>}
                    {p.portfolio_path && <a href={fileUrl(p.portfolio_path)} target="_blank" rel="noreferrer" className="p-2 rounded-md border border-white/10 hover:border-sky-400 text-sky-300" title="Portfolio"><FileCheck2 className="w-4 h-4" /></a>}
                    <button onClick={() => setSelected(p)} className="text-xs font-semibold text-sky-300 hover:text-sky-200 px-2" data-testid={`mmp-row-view-${p.unique_id}`}>View</button>
                    <button onClick={() => remove(p)} className="p-2 rounded-md border border-white/10 hover:border-red-500 text-red-400" title="Delete" data-testid={`mmp-row-del-${p.unique_id}`}><Trash2 className="w-4 h-4" /></button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* PAGINATION */}
        <div className="flex items-center justify-between text-sm text-slate-400">
          <div>{total} profile{total === 1 ? "" : "s"} · page {page} of {pages}</div>
          <div className="flex gap-2">
            <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1} className="px-3 py-1.5 rounded-md border border-white/10 disabled:opacity-40 inline-flex items-center gap-1"><ChevronLeft className="w-4 h-4" /> Prev</button>
            <button onClick={() => setPage((p) => Math.min(pages, p + 1))} disabled={page >= pages} className="px-3 py-1.5 rounded-md border border-white/10 disabled:opacity-40 inline-flex items-center gap-1">Next <ChevronRight className="w-4 h-4" /></button>
          </div>
        </div>
      </div>

      {/* DETAIL DRAWER */}
      {selected && <DetailDrawer p={selected} onClose={() => setSelected(null)} fileUrl={fileUrl} />}
      {showTemplates && <TemplatesModal onClose={() => setShowTemplates(false)} />}
    </div>
  );
}

function Sel({ value, onChange, disabled, testid, children }) {
  return (
    <select value={value} onChange={(e) => onChange(e.target.value)} disabled={disabled} data-testid={testid} className="rounded-lg border border-white/10 bg-[#0B0F1A] px-3 py-2.5 text-sm focus:outline-none disabled:opacity-40">{children}</select>
  );
}

function Pill({ className = "", children }) { return <span className={`text-[0.65rem] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-full ${className}`}>{children}</span>; }

function Stars({ rating, onSet }) {
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((r) => (
        <button key={r} onClick={() => onSet(r)} className={r <= rating ? "text-amber-400" : "text-slate-600 hover:text-amber-400"}><Star className="w-3.5 h-3.5" fill={r <= rating ? "currentColor" : "none"} /></button>
      ))}
    </div>
  );
}

function DocBadge({ label, ok }) {
  return <span className={`inline-flex items-center gap-1 text-[0.6rem] font-bold uppercase tracking-widest px-1.5 py-0.5 rounded ${ok ? "bg-emerald-500/15 text-emerald-300" : "bg-white/5 text-slate-500"}`}>{ok ? "✓" : "—"} {label}</span>;
}

function Avatar({ path, name }) {
  const [err, setErr] = useState(false);
  const url = path ? `${API}/files/${path}` : null;
  if (!url || err) {
    return <div className="w-10 h-10 rounded-full bg-sky-500/20 text-sky-300 flex items-center justify-center font-bold text-sm shrink-0">{initials(name)}</div>;
  }
  return <img src={url} onError={() => setErr(true)} alt={name} className="w-10 h-10 rounded-full object-cover bg-white/5 shrink-0" />;
}

function DetailDrawer({ p, onClose, fileUrl }) {
  return (
    <div className="fixed inset-0 z-40 bg-black/60 flex justify-end" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="w-full max-w-md bg-[#0E1525] border-l border-white/10 overflow-y-auto" data-testid="mmp-admin-drawer">
        <div className="p-5 border-b border-white/10 flex items-center justify-between">
          <div>
            <div className="font-mono text-xs text-slate-500">{p.unique_id}</div>
            <div className="font-display font-black text-xl">{p.full_name}</div>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-white"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5 space-y-3 text-sm">
          {[
            ["Email", p.email], ["Phone", p.phone], ["Gender", p.gender], ["DOB", p.date_of_birth],
            ["Age", p.age], ["Profession", p.profession + (p.other_profession ? ` — ${p.other_profession}` : "")],
            ["Experience", p.experience], ["Skills", p.skills], ["Country", p.country],
            ["State", p.state], ["City", p.city], ["Address", p.address], ["Summary", p.summary],
            ["Notes", p.admin_notes], ["Status", p.status], ["Rating", `${p.rating || 0}★`],
            ["Recommended", p.recommended ? "Yes" : "No"], ["Created", p.created_at],
          ].map(([k, v]) => v ? (
            <div key={k}><div className="text-[0.62rem] uppercase tracking-widest text-slate-500">{k}</div><div className="text-slate-100 whitespace-pre-wrap">{v}</div></div>
          ) : null)}
          <div className="pt-4 space-y-2">
            {p.cv_path && <a href={fileUrl(p.cv_path)} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-sky-300 hover:text-sky-200"><FileText className="w-4 h-4" /> Download CV <ExternalLink className="w-3 h-3" /></a>}
            {p.passport_photo_path && <a href={fileUrl(p.passport_photo_path)} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-sky-300 hover:text-sky-200"><ImgIcon className="w-4 h-4" /> View Photo <ExternalLink className="w-3 h-3" /></a>}
            {p.portfolio_path && <a href={fileUrl(p.portfolio_path)} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-sky-300 hover:text-sky-200"><FileCheck2 className="w-4 h-4" /> Portfolio <ExternalLink className="w-3 h-3" /></a>}
          </div>
        </div>
      </div>
    </div>
  );
}

function TemplatesModal({ onClose }) {
  const [tpls, setTpls] = useState([]);
  const [label, setLabel] = useState("");
  const [message, setMessage] = useState("");
  const load = async () => { const { data } = await ax.get("/admin-mmp/templates"); setTpls(data); };
  useEffect(() => { load(); }, []);
  const add = async () => { if (!label || !message) return toast.info("Label + message required"); await ax.post("/admin-mmp/templates", { label, message }); setLabel(""); setMessage(""); await load(); toast.success("Template added"); };
  const del = async (t) => { if (!window.confirm(`Delete "${t.label}"?`)) return; await ax.delete(`/admin-mmp/templates/${t.template_id}`); await load(); };
  return (
    <div className="fixed inset-0 z-40 bg-black/60 flex items-start justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="w-full max-w-2xl bg-[#0E1525] border border-white/10 rounded-xl mt-10" data-testid="mmp-templates-modal">
        <div className="p-5 border-b border-white/10 flex items-center justify-between">
          <div className="font-display font-black text-lg">WhatsApp Templates</div>
          <button onClick={onClose} className="text-slate-400 hover:text-white"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5 space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-[1fr,2fr,auto] gap-2">
            <input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Label" className="rounded-md border border-white/10 bg-[#0B0F1A] px-3 py-2 text-sm focus:outline-none" data-testid="mmp-tpl-label" />
            <input value={message} onChange={(e) => setMessage(e.target.value)} placeholder="Message with {name} and {id}" className="rounded-md border border-white/10 bg-[#0B0F1A] px-3 py-2 text-sm focus:outline-none" data-testid="mmp-tpl-message" />
            <button onClick={add} className="bg-sky-500 hover:bg-sky-400 text-[#0B0F1A] font-bold text-xs uppercase tracking-widest px-3 py-2 rounded-md" data-testid="mmp-tpl-add">Add</button>
          </div>
          <ul className="mt-3 divide-y divide-white/5 max-h-[50vh] overflow-y-auto">
            {tpls.map((t) => (
              <li key={t.template_id} className="py-3 flex items-start gap-3">
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-sky-300">{t.label}</div>
                  <div className="text-xs text-slate-300 mt-0.5">{t.message}</div>
                </div>
                <button onClick={() => del(t)} className="text-slate-400 hover:text-red-400" data-testid={`mmp-tpl-del-${t.template_id}`}><Trash2 className="w-4 h-4" /></button>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}
